import { useCanvas } from "@/hooks/useCanvas";
import { useVotingStatus } from "@/hooks/useVotingStatus";
import { routes } from "@/routes/paths";
import { usePriorityStore } from "@/stores/priorityStore";
import { completeParticipation, putParticipationVotes } from "@/services/participationApi";
import { getOrCreateAnonymousVoterId } from "@canucci/base/canvas";
import { useAuth } from "@canucci/base/auth";
import {
  accentDividerStyle,
  darkSurfaceOverlayProps,
  darkSurfaceModalStyles,
  filledCtaButtonProps,
  surfaceStatusBannerProps,
  surfaceStatusIconProps,
} from "@canucci/base-mantine/theme";
import { Box, Button, Group, Anchor, Modal, Text, Stack, Paper, ThemeIcon, useMantineTheme } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { useLocation, useParams } from "react-router-dom";
import {
  participationVoteSyncKey,
  queueParticipationVoteSubmission,
  snapshotFromSelection,
} from "./participationVoteSync";
import { PriorityItem } from "./PriorityItem";

interface PrioritiesModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when the modal should be closed
   */
  onClose: () => void;
}

function PrioritiesModal({ open, onClose }: PrioritiesModalProps) {
  const { user } = useAuth();
  const theme = useMantineTheme();
  // Use selectors for state to prevent re-rendering the whole modal on comment input
  const selectedTopicIds = usePriorityStore((state) => state.selectedTopicIds);
  const modalMessage = usePriorityStore((state) => state.modalMessage);
  const isSubmitted = usePriorityStore((state) => state.isSubmitted);
  const comments = usePriorityStore((state) => state.comments);
  const submit = usePriorityStore((state) => state.submit);
  const maxSelections = usePriorityStore((state) => state.maxSelections);
  const { canvasId } = useParams<{ canvasId: string }>();
  const { canvasResult, isParticipation, participationToken } = useCanvas();
  const location = useLocation();
  const { t: tPages } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");

  // Check if voting is closed
  const { isVotingClosed } = useVotingStatus();

  // Check if we're on the insights page
  const isOnInsightsPage = location.pathname.includes("/insights");

  const selectedCount = selectedTopicIds.length;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (!isParticipation || !participationToken || !canvasResult.data) return;
    setIsSubmitting(true);
    try {
      const identity =
        canvasResult.data.voting?.access_mode === "authenticated"
          ? { authToken: await user?.getIdToken() }
          : { voterId: getOrCreateAnonymousVoterId(participationToken) };
      if (!identity.authToken && !identity.voterId) return;
      const accessMode = canvasResult.data.voting?.access_mode === "authenticated" ? "authenticated" : "anonymous";
      const identityKey = accessMode === "authenticated" ? user?.uid : identity.voterId;
      if (!identityKey) return;
      const syncKey = participationVoteSyncKey({
        token: participationToken,
        accessMode,
        identityKey,
      });
      await queueParticipationVoteSubmission(
        syncKey,
        snapshotFromSelection(selectedTopicIds, comments),
        (snapshot) => {
          const selected = new Set(snapshot.ids);
          const votes = (canvasResult.data.themes ?? []).flatMap((theme) =>
            (theme.subthemes ?? []).map((subtheme) => ({
              target_id: subtheme.id,
              selected: selected.has(subtheme.id),
              comment: selected.has(subtheme.id) ? snapshot.comments[subtheme.id] || null : null,
            })),
          );
          return [...votes.filter((vote) => !vote.selected), ...votes.filter((vote) => vote.selected)];
        },
        (votes) => putParticipationVotes(participationToken, identity, votes),
      );
      await completeParticipation(participationToken, identity);
      submit();
      onClose();
      // Submission confirmation is action feedback, so show it after every successful submission.
      notifications.show({
        title: tCommon("priorities.toasts.submittedTitle"),
        message: tCommon("priorities.toasts.submittedDescription"),
        autoClose: 4000,
      });
    } catch (error) {
      console.error("Failed to submit participation votes", error);
      notifications.show({
        color: "red",
        title: tPages("errors.generic.title"),
        message: tPages("errors.generic.message"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="md"
      centered
      zIndex={1100}
      overlayProps={darkSurfaceOverlayProps}
      title={
        <Stack gap={0}>
          <Text fw={700} fz="lg" c="var(--text-on-dark)">
            {isVotingClosed
              ? tPages("canvas.insights.modal.title")
              : tPages("canvas.insights.modal.titleWithCount", {
                  count: selectedCount,
                  max: maxSelections,
                })}
          </Text>
          <Box mt="xs" style={accentDividerStyle} />
        </Stack>
      }
      styles={darkSurfaceModalStyles({ paddingBottom: 0 })}
    >
      <Box>
        {modalMessage && (
          <Paper
            mb="md"
            p="xs"
            bg="orange.0"
            radius="md"
            withBorder
            style={{
              borderColor: theme.colors.orange[2],
            }}
          >
            <Text c="orange.8" fz="sm">
              {modalMessage}
            </Text>
          </Paper>
        )}

        {selectedCount === 0 ? (
          <Box style={{ textAlign: "center" }} py="xl">
            <Text c="var(--text-on-dark-muted)">
              {tPages("canvas.insights.modal.emptyState", {
                count: maxSelections,
              })}
            </Text>
          </Box>
        ) : (
          <Box component="ul" m={0} p={0} style={{ listStyleType: "none" }}>
            {selectedTopicIds.map((topicId, index: number) => {
              const isLast: boolean = index === selectedTopicIds.length - 1;
              return (
                <Box component="li" key={topicId}>
                  <PriorityItem topicId={topicId} isLast={isLast} />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Footer with submit button */}
      {selectedCount > 0 && (
        <Box mt="md" mb="md">
          <Stack w="100%" gap="xs">
            {/* Show different message when voting is closed */}
            {isVotingClosed ? (
              <Paper
                w="100%"
                p="xs"
                bg="blue.0"
                radius="md"
                withBorder
                style={{
                  borderColor: theme.colors.blue[2],
                }}
              >
                <Text fz="sm" c="blue.8" ta="center">
                  {tPages("canvas.insights.inputCollectionClosedMessage")}
                </Text>
              </Paper>
            ) : (
              isSubmitted && (
                <Paper {...surfaceStatusBannerProps}>
                  <Group gap="sm" wrap="nowrap">
                    <ThemeIcon {...surfaceStatusIconProps}>
                      <FaCheck size={14} />
                    </ThemeIcon>
                    <Text fz="sm" fw={500} c="var(--text-on-dark)">
                      {tPages("canvas.insights.modal.submittedMessage")}
                    </Text>
                  </Group>
                </Paper>
              )
            )}

            {/* Only show submit/update button when voting is open */}
            {!isVotingClosed && (
              <Button
                {...filledCtaButtonProps("primary")}
                variant="filled"
                fullWidth
                onClick={() => void handleSubmit()}
                loading={isSubmitting}
              >
                {isSubmitted
                  ? tPages("canvas.insights.modal.updateButton")
                  : tPages("canvas.insights.modal.submitButton")}
              </Button>
            )}

            {/* Only show "View team insights" link when NOT on insights page */}
            {!isParticipation && !isOnInsightsPage && canvasId && (
              <Anchor
                component="a"
                href={routes.canvas.insights.path(canvasId)}
                fz="sm"
                c={theme.other.semanticColors.text}
                underline="hover"
                style={{ textAlign: "center", display: "block" }}
              >
                {tPages("canvas.insights.modal.viewInsights")}
              </Anchor>
            )}
          </Stack>
        </Box>
      )}
    </Modal>
  );
}

export default PrioritiesModal;
